
import { supabase } from "@/integrations/supabase/client";
import type { NightlyRate, MinimumStay, PricingSettings, PromoCode } from "@/types/supabase";

type NightlyRateInput = Omit<NightlyRate, 'id' | 'created_at'>;

const normalizeDate = (value: string) => value.slice(0, 10);

const isValidRateRange = (startDate: string, endDate: string) => startDate < endDate;

export const getNightlyRates = async () => {
  const { data, error } = await supabase
    .from('nightly_rates')
    .select('*')
    .order('start_date', { ascending: true });
    
  if (error) throw error;
  return data as NightlyRate[];
};

export const createNightlyRate = async (rate: NightlyRateInput) => {
  const normalizedRate: NightlyRateInput = {
    start_date: normalizeDate(rate.start_date),
    end_date: normalizeDate(rate.end_date),
    price_per_night: rate.price_per_night,
  };

  if (!isValidRateRange(normalizedRate.start_date, normalizedRate.end_date)) {
    throw new Error("La date de fin doit être après la date de début.");
  }

  const { data, error } = await supabase.rpc('replace_nightly_rate_range', {
    p_start_date: normalizedRate.start_date,
    p_end_date: normalizedRate.end_date,
    p_price_per_night: normalizedRate.price_per_night,
  });
    
  if (error) throw error;

  const insertedRates = data as NightlyRate[];
  return insertedRates.find((insertedRate) => (
    normalizeDate(insertedRate.start_date) === normalizedRate.start_date
    && normalizeDate(insertedRate.end_date) === normalizedRate.end_date
    && Number(insertedRate.price_per_night) === Number(normalizedRate.price_per_night)
  )) || insertedRates[insertedRates.length - 1];
};

export const updateNightlyRate = async ({ id, price_per_night }: Pick<NightlyRate, 'id' | 'price_per_night'>) => {
  const { data, error } = await supabase
    .from('nightly_rates')
    .update({ price_per_night })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as NightlyRate;
};

export const deleteNightlyRate = async (id: string) => {
  const { error } = await supabase
    .from('nightly_rates')
    .delete()
    .eq('id', id);
    
  if (error) throw error;
  return true;
};

export const getMinimumStays = async () => {
  const { data, error } = await supabase
    .from('minimum_stays')
    .select('*')
    .order('start_date', { ascending: true });
    
  if (error) throw error;
  return data as MinimumStay[];
};

export const createMinimumStay = async (stay: Omit<MinimumStay, 'id' | 'created_at'>) => {
  const { data, error } = await supabase
    .from('minimum_stays')
    .insert([stay])
    .select()
    .single();
    
  if (error) throw error;
  return data as MinimumStay;
};

export const deleteMinimumStay = async (id: string) => {
  const { error } = await supabase
    .from('minimum_stays')
    .delete()
    .eq('id', id);
    
  if (error) throw error;
  return true;
};

export const getPricingSettings = async () => {
  const { data, error } = await supabase
    .from('pricing_settings')
    .select('*')
    .maybeSingle();
    
  if (error) throw error;
  return data as PricingSettings;
};

export const updatePricingSettings = async (settings: Partial<PricingSettings>) => {
  const { data, error } = await supabase
    .from('pricing_settings')
    .update(settings)
    .eq('id', settings.id)
    .select()
    .single();
    
  if (error) throw error;
  return data as PricingSettings;
};

export const getPromoCodes = async () => {
  // Cette requête devrait maintenant fonctionner pour tous les utilisateurs
  // grâce à la politique RLS que nous avons ajoutée
  const { data, error } = await supabase
    .from('promo_codes')
    .select('*')
    .order('created_at', { ascending: false });
    
  if (error) {
    console.error("Erreur lors de la récupération des codes promo:", error);
    throw error;
  }
  return data as PromoCode[];
};

export const createPromoCode = async (code: Omit<PromoCode, 'id' | 'created_at' | 'times_used'>) => {
  const { data, error } = await supabase
    .from('promo_codes')
    .insert([code])
    .select()
    .single();
    
  if (error) throw error;
  return data as PromoCode;
};

export const deletePromoCode = async (id: string) => {
  const { error } = await supabase
    .from('promo_codes')
    .delete()
    .eq('id', id);
    
  if (error) throw error;
  return true;
};
